/**
 * Widths for the console's draggable side panels.
 *
 * A split is a per-browser preference, not account state: two people looking at
 * the same project should not share one layout. So it lives in `localStorage`
 * beside the console's other client-side preferences, behind the same injected
 * storage seam `projectSelection` and `navigationPreference` use.
 *
 * The clamp is deliberately separate from the drag: a stored width has to
 * survive being carried to a narrower window, and the panel that was fine at
 * 1440px must not push the document off the screen at 1024px.
 */

interface PanelWidthStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type PanelWidthStorageProvider = () => PanelWidthStorage;

/** One entry per panel. Panels never share a stored width. */
export type PanelWidthKey =
  | 'docs-tree'
  | 'document-comments'
  | 'trackers-pane'
  | 'trackers-detail'
  | 'wiki-tree';

const STORAGE_PREFIX = 'nimbalyst:web-console:panel-width';

/**
 * An absolute sanity ceiling, independent of any viewport. The live clamp below
 * is what keeps a width *usable*; this only rejects a value no drag could have
 * produced, so a hand-edited or corrupted entry cannot paint one broken frame
 * before the container has been measured.
 */
const MAX_STORED_WIDTH = 2_000;

export interface PanelWidthBounds {
  min: number;
  max: number;
}

export interface PanelWidthSpec {
  /** The narrowest the panel is still worth showing at. */
  min: number;
  /** Room the panel must leave for whatever sits beside it. */
  minComplement: number;
}

function storageKey(panel: PanelWidthKey): string {
  return `${STORAGE_PREFIX}:${panel}`;
}

/**
 * Bounds for a panel sharing `containerWidth` with its neighbour.
 *
 * Derived from the measured container rather than from fixed pixel constants,
 * because the constant that leaves a comfortable document at 1440px leaves an
 * unreadable one at 1024px.
 */
export function panelWidthBounds(containerWidth: number, spec: PanelWidthSpec): PanelWidthBounds {
  // Nothing has been measured yet (first frame, or a hidden container). Falling
  // back to `min` here would snap a restored width narrow and then animate it
  // back out; the stored-value ceiling is what guards this frame instead.
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return { min: spec.min, max: MAX_STORED_WIDTH };
  }
  // On a container too narrow for both, the panel stops at `min` and the
  // neighbour takes the squeeze — the panel never wins that trade.
  return { min: spec.min, max: Math.max(spec.min, containerWidth - spec.minComplement) };
}

export function clampPanelWidth(width: number, bounds: PanelWidthBounds): number {
  if (!Number.isFinite(width)) return bounds.min;
  // `max` is floored at `min` so an inverted pair can't produce a width below
  // the minimum via `Math.min`.
  return Math.min(Math.max(width, bounds.min), Math.max(bounds.min, bounds.max));
}

function browserStorage(): PanelWidthStorage {
  return window.localStorage;
}

/** The stored width, or `defaultWidth` when it is absent, unparseable, or absurd. */
export function readPanelWidth(
  panel: PanelWidthKey,
  defaultWidth: number,
  getStorage: PanelWidthStorageProvider = browserStorage,
): number {
  try {
    const raw = getStorage().getItem(storageKey(panel));
    if (raw === null) return defaultWidth;
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed) || parsed <= 0 || parsed > MAX_STORED_WIDTH) return defaultWidth;
    return parsed;
  } catch {
    // Site data can be disabled; the default still gives a working layout.
    return defaultWidth;
  }
}

export function rememberPanelWidth(
  panel: PanelWidthKey,
  width: number,
  getStorage: PanelWidthStorageProvider = browserStorage,
): void {
  if (!Number.isFinite(width) || width <= 0) return;
  try {
    getStorage().setItem(storageKey(panel), String(Math.round(width)));
  } catch {
    // The drag still works for this session when persistence is unavailable.
  }
}
